// src/plugins/builder/storage.ts
// IndexedDB store for user-built plugins.
//
// Why IndexedDB and not localStorage? Some templates (PDF report, ZIP exports,
// generated source) push the saved state past localStorage's per-key string
// budget. IDB is also async-friendly and gets us blob storage for free.
//
// Falls back to in-memory + localStorage when IDB is unavailable (Node tests,
// older Safari, private mode). The fallback exposes the same API so the rest
// of the code never has to care.
const DB_NAME = 'reviewradar.builder';
const DB_VERSION = 1;
const STORE = 'user-plugins';
const LS_KEY = 'reviewradar.builder.user-plugins.v1';
const listeners = new Set();
function emit(records) {
    for (const fn of listeners) {
        try {
            fn(records);
        }
        catch { /* ignore */ }
    }
}
export function subscribe(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}
// ----- IndexedDB plumbing ---------------------------------------------------
function hasIDB() {
    return typeof globalThis !== 'undefined'
        && typeof globalThis.indexedDB !== 'undefined';
}
function openDb() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE)) {
                db.createObjectStore(STORE, { keyPath: 'instanceId' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed.'));
    });
}
async function withStore(mode, fn) {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const st = tx.objectStore(STORE);
        const req = fn(st);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IDB request failed.'));
        tx.oncomplete = () => db.close();
        tx.onerror = () => reject(tx.error ?? new Error('IDB transaction failed.'));
    });
}
// ----- localStorage fallback ------------------------------------------------
function readFromLs() {
    try {
        if (typeof localStorage === 'undefined')
            return memoryStore.slice();
        const raw = localStorage.getItem(LS_KEY);
        if (!raw)
            return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    }
    catch {
        return [];
    }
}
function writeToLs(rows) {
    if (typeof localStorage === 'undefined') {
        memoryStore = rows.slice();
        return;
    }
    localStorage.setItem(LS_KEY, JSON.stringify(rows));
}
let memoryStore = [];
// ----- public API -----------------------------------------------------------
/** All user-built plugins, sorted newest-first. */
export async function listUserPlugins() {
    if (!hasIDB())
        return readFromLs().sort(byUpdatedDesc);
    try {
        const rows = await withStore('readonly', (s) => s.getAll());
        return (rows ?? []).slice().sort(byUpdatedDesc);
    }
    catch {
        return readFromLs().sort(byUpdatedDesc);
    }
}
/** Synchronous best-effort read — pulls from the localStorage mirror only.
 *  Used by the registry adapter, which has to be sync to fit the existing
 *  registry contract. The async API above is the source of truth. */
export function listUserPluginsSync() {
    return readFromLs().sort(byUpdatedDesc);
}
export async function getUserPlugin(instanceId) {
    if (!hasIDB())
        return readFromLs().find(r => r.instanceId === instanceId) ?? null;
    try {
        const row = await withStore('readonly', (s) => s.get(instanceId));
        return row ?? null;
    }
    catch {
        return readFromLs().find(r => r.instanceId === instanceId) ?? null;
    }
}
export async function saveUserPlugin(rec) {
    const next = { ...rec, updatedAt: new Date().toISOString() };
    if (hasIDB()) {
        try {
            await withStore('readwrite', (s) => s.put(next));
        }
        catch { /* fall through to ls mirror */ }
    }
    // Always mirror to localStorage so listUserPluginsSync stays current.
    const all = readFromLs().filter(r => r.instanceId !== next.instanceId);
    all.push(next);
    writeToLs(all);
    emit(all.sort(byUpdatedDesc));
    return next;
}
export async function deleteUserPlugin(instanceId) {
    if (hasIDB()) {
        try {
            await withStore('readwrite', (s) => s.delete(instanceId));
        }
        catch { /* ignore */ }
    }
    const all = readFromLs().filter(r => r.instanceId !== instanceId);
    writeToLs(all);
    emit(all);
}
export async function clearAllForTests() {
    if (hasIDB()) {
        try {
            await withStore('readwrite', (s) => s.clear());
        }
        catch { /* ignore */ }
    }
    writeToLs([]);
    memoryStore = [];
    emit([]);
}
function byUpdatedDesc(a, b) {
    return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
}
// Cross-tab sync: any LS write fires storage; rehydrate listeners from it.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.key === LS_KEY)
            emit(readFromLs().sort(byUpdatedDesc));
    });
}
